import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, MenuItem, Chip, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxQcRunDto, type DxQcLevel, type DxQcMeasurementInput } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { formatDateTime } from "@/shared/datetime";

const NIL = "00000000-0000-0000-0000-000000000000";

export function DiagnosticsQcPage() {
  const canRecord = useAuth(s => s.hasPermission("diagnostics.qc.record"));
  const [open, setOpen] = useState(false);
  const query = useQuery({ queryKey: ["dx-qc"], queryFn: () => diagnosticsApi.listQc() });

  const columns: GridColDef<DxQcRunDto>[] = [
    { field: "runAt", headerName: "Run at", width: 190, valueGetter: v => v ? formatDateTime(v as string) : "" },
    { field: "level", headerName: "Level", width: 110 },
    {
      field: "outcome", headerName: "Outcome", width: 130,
      renderCell: p => <Chip size="small" label={p.row.outcome}
        color={p.row.outcome === "Fail" ? "error" : p.row.outcome === "Warning" ? "warning" : "success"} />,
    },
    { field: "analyzerId", headerName: "Analyzer", flex: 1, minWidth: 240 },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Diagnostics · QC Runs</Typography>
        {canRecord && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Record run</Button>}
      </Stack>
      <Paper sx={{ p: 2 }}>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 540 }}>
          <DataGrid rows={query.data ?? []} columns={columns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
      <RecordDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
}

function RecordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [analyzerId, setAnalyzerId] = useState("");
  const [level, setLevel] = useState<DxQcLevel>("Normal");
  const [rows, setRows] = useState<DxQcMeasurementInput[]>([{ analyte: "", value: 0, mean: 0, sd: 1 }]);

  const setRow = (i: number, patch: Partial<DxQcMeasurementInput>) =>
    setRows(rows.map((r, j) => j === i ? { ...r, ...patch } : r));

  const record = useMutation({
    meta: { successMessage: "QC run recorded" },
    mutationFn: () => diagnosticsApi.recordQc({ analyzerId: analyzerId.trim() || NIL, level, measurements: rows }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["dx-qc"] }); setRows([{ analyte: "", value: 0, mean: 0, sd: 1 }]); onClose(); },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Record QC run</DialogTitle>
      <DialogContent dividers>
        {record.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(record.error)}</Alert>}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField label="Analyzer Id (GUID)" size="small" value={analyzerId} onChange={e => setAnalyzerId(e.target.value)} placeholder={NIL} sx={{ flexGrow: 1 }} />
          <TextField select label="Level" size="small" value={level} onChange={e => setLevel(e.target.value as DxQcLevel)} sx={{ width: 140 }}>
            <MenuItem value="Low">Low</MenuItem><MenuItem value="Normal">Normal</MenuItem><MenuItem value="High">High</MenuItem>
          </TextField>
        </Stack>
        <Table size="small">
          <TableHead><TableRow><TableCell>Analyte</TableCell><TableCell>Value</TableCell><TableCell>Mean</TableCell><TableCell>SD</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell><TextField size="small" value={r.analyte} onChange={e => setRow(i, { analyte: e.target.value })} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.value} onChange={e => setRow(i, { value: Number(e.target.value) })} sx={{ width: 100 }} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.mean} onChange={e => setRow(i, { mean: Number(e.target.value) })} sx={{ width: 100 }} /></TableCell>
                <TableCell><TextField size="small" type="number" value={r.sd} onChange={e => setRow(i, { sd: Number(e.target.value) })} sx={{ width: 90 }} /></TableCell>
                <TableCell align="right"><IconButton size="small" onClick={() => setRows(rows.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setRows([...rows, { analyte: "", value: 0, mean: 0, sd: 1 }])}>Add measurement</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={record.isPending || rows.length === 0}
          startIcon={record.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => record.mutate()}>Record</Button>
      </DialogActions>
    </Dialog>
  );
}
